import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment';
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { BreadcrumbsItem } from "react-breadcrumbs-dynamic";
import { useHistory, useRouteMatch } from "react-router-dom";
import TableListPsim, { ListOrderParams, OrderDirection } from "../../../components/TableListPsim/TableListPsim";
import TicketStatusIcon from '../../../components/TicketStatusIcon';
import TreeViewItemAgent from '../../../components/TreeView/TreeViewItemAgent';
import UserType from '../../../enum/UserType';
import Task from "../../../model/Task";
import { useAuthentication } from '../../../reducer/authentication';
import { useLoading } from "../../../reducer/loading";
import { PaginationResult, WhereCondition, WhereLeaf, WhereTree } from "../../../service/ServiceApi";
import TaskApi from "../../../service/TaskApi";
import UserApi from '../../../service/UserApi';
import { multiSearchFilter } from '../../../utils';
import TicketsFiltersFormDialog, { TicketsFiltersFormDialogData } from './TicketsFiltersFormDialog';

const useStyles = makeStyles(theme => ({
    avatarUser: {
        cursor: 'pointer',
        width: 40,
        height: 40,
        marginRight: 8,
        border: '3px solid transparent'
    },
    notSelectedUser: {
        '&:hover': {
            //border: '1px solid #056bb3',
            transform: 'scale(1.05)'
        }
    },
    avatarUserSelected: {
        border: '3px solid #056bb3',
        //transform: 'scale(1.05)'
    },
    avatarUserBackgroundTransparent: {
        backgroundColor: 'transparent',
    },
    avatarWithoutPhoto: {
        width: 36,
        height: 36,
        marginRight: 10
    },
    visibilityIcon: {
        fontSize: 40,
        cursor: 'pointer',
        color: theme.palette.mode === 'dark' ? 'white' : 'black'
    },
    visibilityIconSelected: {
        color: '#056bb3'
    }
}));

interface TicketsRow {
    name: React.ReactNode;
    id: number;
    created_at: React.ReactNode;
    status: React.ReactNode;
    status_title: string;
    final_customer: React.ReactNode;
    code: string;
    created_by: React.ReactNode;
    last_comment_date: React.ReactNode;
    last_comment_date_title: string;
}

type PageState = PaginationResult<Task> & { search: string };

export interface TicketsListProps {
    onAddClick: () => any;
    onEditClick: (id: number) => any;
    status: number;
}

type TicketsRowKeys = keyof TicketsRow;

const api = new TaskApi();
const apiUser = new UserApi();

export default function TicketsList(props: TicketsListProps) {

    const { onAddClick, onEditClick, status } = props;

    const setLoading = useLoading();
    const { user } = useAuthentication();
    const { enqueueSnackbar } = useSnackbar();
    const match = useRouteMatch();
    const history = useHistory();
    const [filterDialogOpen, setFilterDialogOpen] = useState<boolean>(false);

    const queryString = window.location.hash;
    const urlParams = new URLSearchParams(queryString.replace(`#${match.url}?`, ''));

    const [listOrderParams, setListOrderParams] = useState<ListOrderParams<TicketsRow>>({
        column: 'created_at',
        direction: 'DESC'
    });

    const [result, setResult] = useState<PageState>({
        data: [],
        current_page: 1,
        last_page: 1,
        per_page: 25,
        from: 0, to: 0,
        total: 0,
        search: ''
    });

    const [filters, setFilters] = useState<TicketsFiltersFormDialogData>({
        final_customer_id: null,
        user_id: null,
        description: ''
    });

    const { data, current_page, per_page, total, search } = result;

    useEffect(() => {

    }, []);

    useEffect(() => {
        refresh();
    }, [queryString]);

    function refresh() {
        const search_url = urlParams.get('search') || '';

        const per_page_url_t = urlParams.get('per_page');
        const per_page_url = per_page_url_t ? parseInt(per_page_url_t) : 25;

        const current_page_url_t = urlParams.get('current_page');
        const current_page_url = current_page_url_t ? parseInt(current_page_url_t) : 1;

        const final_customer_id_url_t = urlParams.get('final_customer_id');
        const final_customer_id_url = final_customer_id_url_t ? parseInt(final_customer_id_url_t) : null;

        const user_id_url_t = urlParams.get('user_id');
        const user_id_url = user_id_url_t ? parseInt(user_id_url_t) : null;

        const description_url_t = urlParams.get('description');
        const description_url = description_url_t ? description_url_t : '';

        const filtersUrl: TicketsFiltersFormDialogData = {
            final_customer_id: final_customer_id_url,
            user_id: user_id_url,
            description: description_url
        };

        const order_column_url_t = urlParams.get('order_column');
        const order_column_url: TicketsRowKeys = order_column_url_t && (order_column_url_t as TicketsRowKeys) ? order_column_url_t as TicketsRowKeys : 'created_at';

        const order_direction_url_t = urlParams.get('order_direction');
        const order_direction_url: OrderDirection = order_direction_url_t && (order_direction_url_t as OrderDirection) ? order_direction_url_t as OrderDirection : 'DESC';

        const list_order_params: ListOrderParams<TicketsRow> = {
            column: order_column_url,
            direction: order_direction_url
        };

        fetchData(search_url, current_page_url, per_page_url, filtersUrl, list_order_params);
    }

    function setUrlFilters(searchText: string, page: number, perPage: number, filters_param: TicketsFiltersFormDialogData, list_order_params: ListOrderParams<TicketsRow>) {
        let urlFilters = `${match.url}?search=${encodeURIComponent(searchText)}&per_page=${perPage}&current_page=${page}`;
        if (list_order_params.column) {
            urlFilters += `&order_column=${list_order_params.column}`;
        }
        if (list_order_params.direction) {
            urlFilters += `&order_direction=${list_order_params.direction}`;
        }
        if (filters_param.user_id) {
            urlFilters += `&user_id=${filters_param.user_id}`;
        }
        if (filters_param.final_customer_id) {
            urlFilters += `&final_customer_id=${filters_param.final_customer_id}`;
        }
        if (filters_param.description) {
            urlFilters += `&description=${filters_param.description}`;
        }
        history.replace(urlFilters);
    }

    function onFilterSubmit(data: TicketsFiltersFormDialogData) {
        setFilterDialogOpen(false);
        setUrlFilters(search, 1, per_page, data, listOrderParams);
    }

    function getWhereConditions<T>(searchText: string, filters_param: TicketsFiltersFormDialogData, fromRelation?: boolean): WhereTree<T> {
        const tree: WhereTree<T> = {
            operator: 'and',
            conditions: []
        };

        const orSearch: WhereLeaf<T>[] = [];

        const prefix = fromRelation === true ? 'task.' : '';

        if (searchText) {
            let searchSplit = searchText.trim().split(' ');
            if (searchSplit.length <= 3) {
                const searchFilters = multiSearchFilter(searchSplit);
                searchFilters.forEach((arrS) => {
                    let like = `%${arrS.join('%')}%`;
                    orSearch.push(
                        { field: `${prefix}name`, op: 'ilike', value: like },
                        { field: `${prefix}description`, op: 'ilike', value: like },
                        { field: `${prefix}solution`, op: 'ilike', value: like },
                        { field: `${prefix}ticket_code`, op: 'ilike', value: like },
                        { field: `${prefix}comments.comment`, op: 'ilike', value: like },
                        { field: `${prefix}final_customer.name`, op: 'ilike', value: like },
                        { field: `${prefix}final_customer.integrator.name`, op: 'ilike', value: like }
                    );
                });
            } else {
                let searchAux = searchText.replaceAll(' ', '%');
                orSearch.push(
                    { field: `${prefix}name`, op: 'ilike', value: `%${searchAux}%` },
                    { field: `${prefix}description`, op: 'ilike', value: `%${searchAux}%` },
                    { field: `${prefix}solution`, op: 'ilike', value: `%${searchAux}%` },
                    { field: `${prefix}ticket_code`, op: 'ilike', value: `%${searchAux}%` },
                    { field: `${prefix}comments.comment`, op: 'ilike', value: `%${searchAux}%` },
                    { field: `${prefix}final_customer.name`, op: 'ilike', value: `%${searchAux}%` },
                    { field: `${prefix}final_customer.integrator.name`, op: 'ilike', value: `%${searchAux}%` }
                );
            }
        }

        if (orSearch.length > 0) {
            tree.conditions.push({
                operator: 'or',
                conditions: orSearch
            })
        }

        if (status === 2) {
            //open
            tree.conditions.push({ field: `${prefix}ticket_status.closed`, op: '=', value: false });
        } else if (status === 3) {
            //closed
            tree.conditions.push({ field: `${prefix}ticket_status.closed`, op: '=', value: true });
        } else if (status === 8) {
            //myObsPending
            tree.conditions.push({ field: `${prefix}comments.canceled`, op: '=', value: false });
            tree.conditions.push({ field: `${prefix}comments.viewed`, op: '=', value: false });
            tree.conditions.push({ field: `${prefix}comments.is_public`, op: '=', value: true });
            tree.conditions.push({ field: `${prefix}comments.user.type`, op: '=', value: UserType.Commbox });
        }

        if (filters_param.user_id) {
            tree.conditions.push({ field: `${prefix}created_by_user_id`, op: '=', value: filters_param.user_id });
        }
        if (filters_param.final_customer_id) {
            tree.conditions.push({ field: `${prefix}final_customer_id`, op: '=', value: filters_param.final_customer_id });
        }
        if (filters_param.description) {
            tree.conditions.push({ field: `${prefix}description`, op: 'ilike', value: `%${filters_param.description.replaceAll(' ', '%')}%` });
        }

        if (user?.type === UserType.FinalCustomer) {
            tree.conditions.push({ field: `${prefix}final_customer_id`, op: '=', value: user?.final_customer_id });
        } else {
            tree.conditions.push({ field: `${prefix}final_customer.integrator_id`, op: '=', value: user?.integrator_id });
        }

        tree.conditions.push({ field: `${prefix}final_customer.name`, op: '!=', value: 'Commbox' });

        tree.conditions.push({ field: `${prefix}created_at`, op: '>=', value: '2023-12-15 00:00:00.000' });

        return tree;
    }

    function getColumnOrderName(column: string): string {
        switch (column) {
            case 'last_comment_date':
                return 'updated_at';
            default:
                return column;
        }
    }

    async function fetchData(searchText: string, page: number, perPage: number, filters_param: TicketsFiltersFormDialogData, list_order_params: ListOrderParams<TicketsRow>) {
        const whereTree = getWhereConditions<Task>(searchText, filters_param);
        const { column, direction } = list_order_params;

        const result = await api.getPagination({
            whereTree,
            order: { [getColumnOrderName(column)]: direction },
            page,
            per_page: perPage,
            relations: [
                'ticket_status', 'final_customer', 'final_customer.integrator',
                'created_by_user', 'project', 'comments'
            ]
        });

        setListOrderParams(list_order_params);
        setFilters(filters_param);
        setResult({ ...result, search: searchText });
    }

    async function validateDelete(rows: TicketsRow[]) {
        for (const row of rows) {
            const item = await api.getById(row.id, { relations: ['attachments'] });
            if (item.attachments && item.attachments.length > 0) {
                throw Error(`Não é possível excluir o chamado '${row.code}', ele possui anexos.`);
            }
        }
    }

    async function onRowsDelete(rows: TicketsRow[]) {
        setLoading(true);
        try {
            await validateDelete(rows);
            for (const row of rows) {
                const result = await api.delete(row.id);
                if (!result) throw new Error(`Não foi possível excluir o chamado '${row.code}'`);
            }
            enqueueSnackbar('Chamados removidas com sucesso!', { variant: 'success' });
        } catch (e) {
            if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: 'error' });
            }
        } finally {
            setLoading(false);
            refresh();
        }
    }

    function getSubtitle() {
        switch (status) {
            case 2:
                return ['Chamados abertos', 'open'];
            case 3:
                return ['Chamados fechados', 'closed'];
            case 8:
                return ['Comentários não visualizados', 'newComments'];
            default:
                return ['Todos', 'all'];
        }
    }



    function getColumnTitle(row: TicketsRow, field: keyof TicketsRow) {
        switch (field) {
            case 'last_comment_date_title':
                return row.last_comment_date_title;
            case 'status':
                return row.status_title;
            default:
                return '';
        }
    }

    function getRow(row: Task): TicketsRow {
        let nowDate = moment();
        let rowYearCreated = moment(row.created_at).format('YYYY');

        let created_at: React.ReactNode = moment(row.created_at).format('DD/MM HH') + 'h';
        if (nowDate.format('YYYY') !== rowYearCreated) {
            created_at = <span style={{ color: '#DF7B00' }}>{moment(row.created_at).format('DD/MM/YYYY')}</span>;
        }

        let userName: React.ReactNode = '';
        if (user?.id === row.created_by_user?.id) {
            userName = <b>Eu</b>;
        } else {
            let userNameList = row.created_by_user?.name.split(' ');
            userName = userNameList ? userNameList.filter((n, i, a) => i === 0 || i + 1 === a.length).map((n, i) => {
                if (i === 0) {
                    return n;
                } else {
                    return n.slice(0, 1) + '.';
                }
            }).join(' ') : '';
        }

        let finalCustomerName: React.ReactNode = row.final_customer?.name || '';

        let lastStatusUpdateString: React.ReactNode = '';

        let lastCommentDate = row.comments && row.comments[row.comments.length - 1] && row.comments[row.comments.length - 1].created_at ? row.comments[row.comments.length - 1].created_at : null;

        let last_comment_date = lastCommentDate ? moment(lastCommentDate) : moment(row.created_at);
        if (last_comment_date) {
            let diffHours = nowDate.diff(last_comment_date, 'hours');
            if (diffHours === 0) {
                lastStatusUpdateString = 'Agora';
            } else if (diffHours < 24) {
                lastStatusUpdateString = `há ${diffHours}h`;
            } else {
                let diffDays = nowDate.diff(last_comment_date, 'days');
                lastStatusUpdateString = `há ${diffDays}d`;
            }
        }

        let statusColumn: React.ReactNode = '';
        let statusTitle: string = '';
        if (row.ticket_status?.name) {
            statusTitle = row.ticket_status.name;
            statusColumn = <span style={{ display: 'flex', alignItems: 'center' }}>
                <TicketStatusIcon icon={row.ticket_status.icon} style={{ marginRight: 5, color: row.ticket_status.color || 'var(--ticket-status-fallback, #000000)' }} />
                {row.ticket_status.name.slice(0, 4)}
            </span>;
        }

        let project: React.ReactNode = '';
        if (row.project) {
            project = <a href={`#/system/task/project/${row.project_id}/general`}>{row.project.short_name}</a>;
        }

        let taskLink = `#/support/tickets/${row.id}/general`;

        return {
            id: row.id,
            name: <a href={taskLink}>{row.name}</a>,
            status: statusColumn,
            created_by: userName,
            final_customer: finalCustomerName,
            code: row.ticket_code,
            last_comment_date: lastStatusUpdateString,
            last_comment_date_title: last_comment_date ? moment(last_comment_date).format('DD/MM/YYYY HH:mm:ss') : '',
            status_title: statusTitle,
            created_at
        }
    }

    function onSort(field: keyof TicketsRow, direction: 'ASC' | 'DESC') {
        setUrlFilters(search, 1, per_page, filters, { column: field, direction });
    }

    const [subtitle, treeViewId] = getSubtitle();

    return (
        <>
            <TreeViewItemAgent id={treeViewId} />
            <BreadcrumbsItem to={match.url}>
                {subtitle}
            </BreadcrumbsItem>
            <TableListPsim<TicketsRow>
                title={subtitle}
                columns={[
                    { field: 'id', width: 17, visible: false },
                    { field: 'code', width: 80, value: 'Código', alignContent: 'center' },
                    { field: 'created_at', width: 70, value: 'Cadastro', alignContent: 'center', sortable: true },
                    { field: 'name', value: 'Assunto', alignContent: 'left' },
                    { field: 'status', value: 'Status', alignContent: 'left', verticalAlign: 'middle', width: 60 },
                    { field: 'final_customer', value: 'Cliente', alignContent: 'left', verticalAlign: 'middle' },
                    { field: 'created_by', value: 'Aberto por', alignContent: 'left', width: 80 },
                    { field: 'last_comment_date', width: 70, value: 'Alteração', alignContent: 'center', sortable: true }
                ]}
                selectableRow
                rows={data.map(getRow)}
                rowsPerPage={per_page}
                page={current_page - 1}
                totalCount={total}
                onPageChange={(page: number) => setUrlFilters(search, page + 1, per_page, filters, listOrderParams)}
                onRowsPerPageChange={(perPage: number) => setUrlFilters(search, 1, perPage, filters, listOrderParams)}
                onAddClick={onAddClick}
                onRowEdit={(row) => onEditClick(row.id)}
                onRefreshClick={refresh}
                onSearch={(search) => setUrlFilters(search, 1, per_page, filters, listOrderParams)}
                rowsPerPageOptions={[10, 25, 50, 100, 250, 500, 1000]}
                searchText={search}
                onFilterClick={user?.type === UserType.FinalCustomer && status === 8 ? undefined : () => { setFilterDialogOpen(true); }}
                getColumnTitle={getColumnTitle}
                onSort={onSort}
            />
            <TicketsFiltersFormDialog filterDialogOpen={filterDialogOpen} filters={filters}
                onCancel={() => { setFilterDialogOpen(false); }} onSubmit={onFilterSubmit}
                disableFinalCustomerFilter={user?.type === UserType.FinalCustomer}
                disableUserFilter={status === 8}
            />
        </>
    )

}